import { iconIdMapping } from '@arcade/web/icons/react'
import { ArcadeButton, ArcadeButtonProps } from '@cash-design-system/react'
import { ButtonDefault } from '@cash-web/protos/squareup/cash/ui/arcade/elements/ButtonDefault.pb'
import { ButtonProminence } from '@cash-web/protos/squareup/cash/ui/arcade/elements/ButtonProminence.pb'
import logger from '@cash-web/shared/util-logger'

type ArcadeButtonFromButtonDefaultProtoProps = {
  buttonDefault: ButtonDefault
} & Pick<ArcadeButtonProps, 'disabled' | 'isLoading' | 'onClick'>

function mapProminenceFromProto(prominence?: ButtonProminence) {
  switch (prominence) {
    case ButtonProminence.PROMINENT:
      return 'prominent'
    case ButtonProminence.SUBTLE:
      return 'subtle'
    case ButtonProminence.STANDARD:
      return 'standard'
    default:
      // If this error is hit, the client must be updated to provide a valid mapping or
      // the server must be updated to provide a valid prominence.
      logger.error(new Error(`ArcadeButtonFromButtonDefaultProto: unknown prominence: ${prominence}`))
      return 'standard'
  }
}

export function ArcadeButtonFromButtonDefaultProto({
  buttonDefault,
  onClick,
  disabled,
  isLoading,
}: ArcadeButtonFromButtonDefaultProtoProps) {
  const text = buttonDefault.text?.translated_value
  const iconArcadeId = buttonDefault.icon?.arcade_id
  const icon = iconArcadeId ? iconIdMapping[iconArcadeId] : undefined
  const isDestructive = buttonDefault.is_destructive

  if (!text) {
    // If this error is hit, the server must be updated to provide a translated value.
    logger.error(new Error('ArcadeButtonFromButtonDefaultProto: text.translated_value is missing from buttonDefault'))
  }

  if (iconArcadeId && !icon) {
    // If this error is hit, either we need to add a mapping for this iconArcadeId, or the server must be updated to provide a valid iconArcadeId.
    logger.error(new Error(`ArcadeButtonFromButtonDefaultProto: iconArcadeId is missing from mapping: ${iconArcadeId}`))
  }

  if (isDestructive === undefined) {
    // If this error is hit, the server must be updated to provide a value for is_destructive.
    logger.error(new Error('ArcadeButtonFromButtonDefaultProto: is_destructive value is missing from proto'))
  }

  return (
    <ArcadeButton
      prominence={mapProminenceFromProto(buttonDefault.button_prominence)}
      size="default"
      isDestructive={isDestructive ?? false}
      Icon={icon?.Icon}
      onClick={onClick}
      disabled={disabled}
      isLoading={isLoading}
    >
      {text ?? ''}
    </ArcadeButton>
  )
}
